import { useEffect } from "react";

import { uniqBy } from "lodash";

import { useLocation } from "src/router";
import { useUser } from "src/contexts/user-context";
import { usePersistedState } from "src/hooks/use-persisted-state";
import type { UnsavedValue } from "src/hooks/use-unsaved-value";
import {
  marketingPaths,
  activationPaths,
  studioPaths,
  matchCurrentRoute,
  parseRouteToMeta,
  Meta,
} from "src/pages/home/route-utils";

const MAX_RECENTLY_VIEWED = 25;

export const getPersistedKey = (
  workspaceId: string,
  view: "marketing" | "activation" | "studio",
) => `${workspaceId}:${view}:recently-viewed`;

export const useRecentlyViewed = () => {
  const { workspace } = useUser();
  const marketing = usePersistedState(
    getPersistedKey(workspace?.id, "marketing"),
    [] as Meta[],
  );
  const activation = usePersistedState(
    getPersistedKey(workspace?.id, "activation"),
    [] as Meta[],
  );
  const studio = usePersistedState(
    getPersistedKey(workspace?.id, "studio"),
    [] as Meta[],
  );

  const location = useLocation();

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    const matches = matchCurrentRoute(location.pathname);
    if (!matches) return;

    // Record the matched resource into its bucket, most-recently-viewed first,
    // stamped with the view time so the agentic home can rank across buckets.
    const record = (paths: string[], state: UnsavedValue<Meta[]>) => {
      const match = matches.find(
        (m) => m.route.path && paths.includes(m.route.path),
      );
      if (!match?.route.path || !match.params) return;
      const meta = parseRouteToMeta(match.route.path, match.params);
      if (!meta) return;
      const entry: Meta = { ...meta, viewedAt: new Date().toISOString() };
      state.set(
        uniqBy(
          [entry, ...(state.value ?? [])],
          (item) => `${item.resource}-${item.id}`,
        ).slice(0, MAX_RECENTLY_VIEWED),
      );
    };

    record(marketingPaths, marketing);
    record(activationPaths, activation);
    record(studioPaths, studio);
  }, [location]);
};
